import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MoreHorizontal, MessageSquare } from 'lucide-react';
import { UserActionDialogProvider, UserActionMenu } from '../../chat/components';
import { GuildRoleBadge } from './GuildRoleBadge';
import type { GuildMemberResponse, GuildMemberRole, GuildPermission } from '@/lib/api';

interface GuildMemberListProps {
  members: GuildMemberResponse[];
  myRole: GuildMemberRole | null | undefined;
  /**
   * The viewer's resolved-permission predicate (from `useGuildPermissions`).
   * Kick/ban are authorized server-side on `kick_members` / `ban_members` —
   * which custom roles can grant — so gating on base role alone would hide the
   * actions from a member whose custom role legitimately allows them.
   */
  can: (permission: GuildPermission) => boolean;
  onUpdateRole?: (userId: string, role: string) => void;
  onRemove?: (userId: string) => void | Promise<void>;
  onBan?: (userId: string, reason?: string) => void | Promise<void>;
  /**
   * Opens a GUILD-SCOPED DM with the member — a conversation filed under this
   * guild, distinct from any personal DM with the same person.
   */
  onMessage?: (userId: string) => void | Promise<void>;
  /** Suppresses the message action on the viewer's own row. */
  currentUserId?: string | null;
  /** Opens the custom-role picker for a member. Omit to hide the action. */
  onManageRoles?: (member: GuildMemberResponse) => void;
  /**
   * Opens the PERSONAL DM with this member in the chat arche — deliberately not
   * the guild-scoped conversation `onMessage` opens. The name menu is the same
   * one the chat arche shows, and "Message" there has always meant the ordinary
   * one-to-one chat; making it mean something else inside a guild would be the
   * surprise. The guild-scoped conversation keeps its own explicit button.
   */
  onPersonalMessage?: (userId: string) => void | Promise<void>;
}

const ROLE_HIERARCHY: Record<string, number> = { owner: 4, admin: 3, moderator: 2, member: 1 };

export function GuildMemberList({
  members,
  myRole,
  can,
  onUpdateRole,
  onRemove,
  onBan,
  onMessage,
  currentUserId,
  onManageRoles,
  onPersonalMessage,
}: GuildMemberListProps) {
  const myLevel = myRole ? ROLE_HIERARCHY[myRole] || 0 : 0;
  const canKick = can('kick_members');
  const canBan = can('ban_members');
  // Role promote/demote stays base-role gated (custom roles never grant it),
  // so the column can't vanish for an admin while permissions resolve.
  const showActions = canKick || canBan || myLevel >= 3;
  const [banTarget, setBanTarget] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [kickTarget, setKickTarget] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleBanConfirm = async () => {
    if (banTarget) {
      try {
        setActionError(null);
        await onBan?.(banTarget, banReason.trim() || undefined);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to ban member');
      }
      setBanTarget(null);
      setBanReason('');
    }
  };

  const handleBanCancel = () => {
    setBanTarget(null);
    setBanReason('');
  };

  const handleKickConfirm = async () => {
    if (kickTarget) {
      try {
        setActionError(null);
        await onRemove?.(kickTarget);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to kick member');
      }
      setKickTarget(null);
    }
  };

  const handleKickCancel = () => {
    setKickTarget(null);
  };

  return (
    // Owns the person menu's dialogs. Here they would survive the menu
    // closing anyway, but the provider is the single place those dialogs
    // are rendered — a second copy is how two surfaces drift apart.
    <UserActionDialogProvider
      onOpenDm={onPersonalMessage ? (uid) => void onPersonalMessage(uid) : undefined}
    >
      <>
        <Dialog
          open={banTarget !== null}
          onOpenChange={(open) => {
            if (!open) handleBanCancel();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ban Member</DialogTitle>
              <DialogDescription>
                This will ban the user from the guild and remove them from the member list.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="ban-reason">Reason (optional)</Label>
              <Textarea
                id="ban-reason"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Why is this member being banned?"
                maxLength={500}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleBanCancel}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBanConfirm}>
                Ban
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={kickTarget !== null}
          onOpenChange={(open) => {
            if (!open) handleKickCancel();
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Kick Member</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove this member from the guild? They can rejoin if the
                guild is public or they have an invite.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleKickCancel}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleKickConfirm}>
                Kick
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {actionError && <p className="text-sm text-destructive mb-2">{actionError}</p>}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              {onManageRoles && <TableHead>Custom roles</TableHead>}
              {onMessage && <TableHead className="w-[110px]" />}
              {showActions && <TableHead className="w-[50px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => {
              const memberLevel = ROLE_HIERARCHY[member.role] || 0;
              // Resolved permissions say WHAT the viewer may do; the base-role
              // hierarchy still decides WHOM. Mirrors the server rule in
              // guild-operations.ts: strictly outrank the target, or hold the
              // kick/ban grant against a plain `member` — a grant never reaches
              // a moderator or above, so equal ranks keep their protection from
              // each other. Never the viewer's own row.
              const canManage =
                member.role !== 'owner' &&
                member.userId !== currentUserId &&
                (myLevel > memberLevel || member.role === 'member');

              return (
                <TableRow key={member.userId}>
                  <TableCell className="font-mono text-sm">
                    <UserActionMenu
                      userId={member.userId}
                      displayName={member.username || 'Unknown'}
                      username={member.username}
                      currentUserId={currentUserId}
                      onMessage={onPersonalMessage}
                    >
                      <button
                        type="button"
                        className="rounded px-1 -mx-1 text-left hover:bg-muted hover:underline transition-colors"
                        // Distinct from the row's moderation menu ("Actions for
                        // …") — the two sit in the same row and a shared label
                        // would make them indistinguishable to a screen reader.
                        aria-label={`Member options for ${member.username || 'Unknown'}`}
                      >
                        {member.username || 'Unknown'}
                      </button>
                    </UserActionMenu>
                  </TableCell>
                  <TableCell>
                    <GuildRoleBadge role={member.role} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(member.joinedAt).toLocaleDateString()}
                  </TableCell>
                  {onManageRoles && (
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => onManageRoles(member)}
                        className="flex flex-wrap gap-1 items-center text-left cursor-pointer hover:opacity-80"
                        aria-label={`Manage roles for ${member.username || 'Unknown'}`}
                      >
                        {(member.roles ?? []).length === 0 ? (
                          <span className="text-xs text-muted-foreground">Add roles</span>
                        ) : (
                          (member.roles ?? []).map((r) => (
                            <span
                              key={r.roleId}
                              className="text-xs px-1.5 py-0.5 rounded-full border"
                              style={{ borderColor: r.color ?? undefined }}
                            >
                              {r.name}
                            </span>
                          ))
                        )}
                      </button>
                    </TableCell>
                  )}
                  {onMessage && (
                    <TableCell>
                      {member.userId !== currentUserId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Message ${member.username || 'Unknown'} in this guild`}
                          onClick={() => void onMessage(member.userId)}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Message
                        </Button>
                      )}
                    </TableCell>
                  )}
                  {showActions && (
                    <TableCell>
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Actions for ${member.username || 'Unknown'}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {myLevel >= 3 && memberLevel < myLevel && (
                              <>
                                {member.role !== 'admin' && myLevel >= 4 && (
                                  <DropdownMenuItem
                                    onClick={() => onUpdateRole?.(member.userId, 'admin')}
                                  >
                                    Promote to Admin
                                  </DropdownMenuItem>
                                )}
                                {member.role !== 'moderator' && (
                                  <DropdownMenuItem
                                    onClick={() => onUpdateRole?.(member.userId, 'moderator')}
                                  >
                                    {memberLevel > 2
                                      ? 'Demote to Moderator'
                                      : 'Promote to Moderator'}
                                  </DropdownMenuItem>
                                )}
                                {member.role !== 'member' && (
                                  <DropdownMenuItem
                                    onClick={() => onUpdateRole?.(member.userId, 'member')}
                                  >
                                    Demote to Member
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {canKick && (
                              <DropdownMenuItem
                                onClick={() => setKickTarget(member.userId)}
                                className="text-destructive"
                              >
                                Kick
                              </DropdownMenuItem>
                            )}
                            {canBan && (
                              <DropdownMenuItem
                                onClick={() => setBanTarget(member.userId)}
                                className="text-destructive"
                              >
                                Ban
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </>
    </UserActionDialogProvider>
  );
}
